// store/slices/superAdminCompanySlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { superAdminCompanyAPI } from "../../services/api";
import type {
  SuperAdminCompanyState,
  CompanyWithStats,
  CreateCompanyData,
  CreateCompanyResponse,
  UpdateCompanyData,
  PlatformStats,
} from "../../types/superadmin";

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: SuperAdminCompanyState = {
  companies: [],
  currentCompany: null,
  platformStats: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    pages: 0,
    total: 0,
    limit: 20,
  },
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const getAllCompanies = createAsyncThunk(
  "superAdminCompany/getAllCompanies",
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await superAdminCompanyAPI.getAllCompanies(params);
      return response.data.data as {
        companies: CompanyWithStats[];
        pagination: SuperAdminCompanyState["pagination"];
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch companies",
      );
    }
  },
);

export const getCompanyById = createAsyncThunk(
  "superAdminCompany/getCompanyById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await superAdminCompanyAPI.getCompanyById(id);
      return response.data.data.company as CompanyWithStats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch company",
      );
    }
  },
);

/**
 * createCompany
 * Returns the full CreateCompanyResponse so the page can:
 *  - read result.warnings and show toast.warn for each
 *  - read result.adminUser.inviteToken to confirm invite was sent
 *  - navigate to result.company._id
 */
export const createCompany = createAsyncThunk(
  "superAdminCompany/createCompany",
  async (companyData: CreateCompanyData, { rejectWithValue }) => {
    try {
      const response = await superAdminCompanyAPI.createCompany(companyData);
      return response.data.data as CreateCompanyResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create company",
      );
    }
  },
);

export const updateCompany = createAsyncThunk(
  "superAdminCompany/updateCompany",
  async (
    { id, data }: { id: string; data: UpdateCompanyData },
    { rejectWithValue },
  ) => {
    try {
      const response = await superAdminCompanyAPI.updateCompany(id, data);
      return response.data.data.company as CompanyWithStats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update company",
      );
    }
  },
);

export const updateCompanyStatus = createAsyncThunk(
  "superAdminCompany/updateCompanyStatus",
  async (
    { id, status }: { id: string; status: "active" | "inactive" },
    { rejectWithValue },
  ) => {
    try {
      const response = await superAdminCompanyAPI.updateCompanyStatus(
        id,
        status,
      );
      return response.data.data.company as CompanyWithStats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update company status",
      );
    }
  },
);

export const updateCompanyModules = createAsyncThunk(
  "superAdminCompany/updateCompanyModules",
  async (
    {
      id,
      enabledModules,
      updatedAt,
    }: { id: string; enabledModules: string[]; updatedAt?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await superAdminCompanyAPI.updateCompanyModules(
        id,
        enabledModules,
        updatedAt,
      );
      return response.data.data.company as CompanyWithStats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update company modules",
      );
    }
  },
);

export const deleteCompany = createAsyncThunk(
  "superAdminCompany/deleteCompany",
  async (
    { id, confirmSubdomain }: { id: string; confirmSubdomain: string },
    { rejectWithValue },
  ) => {
    try {
      await superAdminCompanyAPI.deleteCompany(id, confirmSubdomain);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete company",
      );
    }
  },
);

export const getCompanyUsers = createAsyncThunk(
  "superAdminCompany/getCompanyUsers",
  async (
    { id, params }: { id: string; params?: Record<string, any> },
    { rejectWithValue },
  ) => {
    try {
      const response = await superAdminCompanyAPI.getCompanyUsers(id, params);
      return response.data.data as { users: any[]; pagination: any };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch company users",
      );
    }
  },
);

export const getPlatformStats = createAsyncThunk(
  "superAdminCompany/getPlatformStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await superAdminCompanyAPI.getPlatformStats();
      return response.data.data as {
        stats: PlatformStats;
        recentCompanies: CompanyWithStats[];
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch platform stats",
      );
    }
  },
);

export const reprovisionDns = createAsyncThunk(
  "superAdminCompany/reprovisionDns",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await superAdminCompanyAPI.reprovisionDns(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reprovision DNS",
      );
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const superAdminCompanySlice = createSlice({
  name: "superAdminCompany",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCompany: (state) => {
      state.currentCompany = null;
    },
    setCurrentCompany: (state, action) => {
      state.currentCompany = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      // ── getAllCompanies ───────────────────────────────────────────────────
      .addCase(getAllCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = action.payload.companies;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ── getCompanyById ───────────────────────────────────────────────────
      .addCase(getCompanyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompanyById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCompany = action.payload;
        state.error = null;
      })
      .addCase(getCompanyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ── createCompany ────────────────────────────────────────────────────
      .addCase(createCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.loading = false;
        // Prepend to list so the new company appears at the top
        if (action.payload.company) {
          state.companies.unshift(action.payload.company);
          state.pagination.total += 1;
        }
        state.error = null;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ── updateCompany ────────────────────────────────────────────────────
      .addCase(updateCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.companies.findIndex(
          (c) => c._id === action.payload._id,
        );
        if (index !== -1) {
          state.companies[index] = {
            ...state.companies[index],
            ...action.payload,
          };
        }
        if (state.currentCompany?._id === action.payload._id) {
          state.currentCompany = { ...state.currentCompany, ...action.payload };
        }
        state.error = null;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ── updateCompanyStatus ──────────────────────────────────────────────
      .addCase(updateCompanyStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // The server normalizes the set before storing it (core modules forced
      // on, orphaned driver surfaces dropped), so what comes back is authoritative
      // and is written over the local copy rather than merged with what was sent.
      .addCase(updateCompanyModules.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.companies.findIndex(
          (c) => c._id === action.payload._id,
        );
        if (index !== -1) {
          state.companies[index] = {
            ...state.companies[index],
            ...action.payload,
          };
        }
        if (state.currentCompany?._id === action.payload._id) {
          state.currentCompany = { ...state.currentCompany, ...action.payload };
        }
      })
      .addCase(updateCompanyModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCompanyStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.companies.findIndex(
          (c) => c._id === action.payload._id,
        );
        if (index !== -1) {
          state.companies[index] = {
            ...state.companies[index],
            ...action.payload,
          };
        }
        if (state.currentCompany?._id === action.payload._id) {
          state.currentCompany = { ...state.currentCompany, ...action.payload };
        }
        state.error = null;
      })
      .addCase(updateCompanyStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ── deleteCompany ────────────────────────────────────────────────────
      .addCase(deleteCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = state.companies.filter(
          (c) => c._id !== action.payload,
        );
        state.pagination.total -= 1;
        if (state.currentCompany?._id === action.payload) {
          state.currentCompany = null;
        }
        state.error = null;
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ── getCompanyUsers ──────────────────────────────────────────────────
      .addCase(getCompanyUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompanyUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentCompany) {
          state.currentCompany = {
            ...state.currentCompany,
            recentUsers: action.payload.users,
          };
        }
        state.error = null;
      })
      .addCase(getCompanyUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ── getPlatformStats ─────────────────────────────────────────────────
      .addCase(getPlatformStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlatformStats.fulfilled, (state, action) => {
        state.loading = false;
        state.platformStats = action.payload.stats;
        state.error = null;
      })
      .addCase(getPlatformStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ── reprovisionDns ───────────────────────────────────────────────────
      // No state changes needed — this is a fire-and-forget action.
      // The page handles success/error toasts directly via .unwrap().
      .addCase(reprovisionDns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reprovisionDns.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(reprovisionDns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentCompany, setCurrentCompany } =
  superAdminCompanySlice.actions;

export default superAdminCompanySlice.reducer;
